import { useMemo } from "react";
import { Archive, ArrowLeft, Forward, Image, Mail, MailOpen, Reply, ReplyAll, Star, Trash2 } from "lucide-react";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { ARCHIVE_FOLDER } from "../../api/mailbox";
import type { MailDetail } from "../../api/types";
import { AttachmentPreview } from "./AttachmentPreview";
import { CalendarCard } from "./CalendarCard";
import { ClientPanel } from "./ClientPanel";
import { FollowupControl } from "./FollowupControl";
import { MailSourceTools } from "./MailSourceTools";
import { ExternalBadge, MailWarnings } from "./MailWarnings";

// 讀信(右欄)。規格 §2.4「讀信」與全域限制:
//
// - HTML 已在後端用 nh3 清過(script / 事件屬性 / 表單拿掉),**再**放進 sandbox iframe。
//   sandbox 只給 `allow-popups allow-popups-to-escape-sandbox`(信裡的連結要能開新分頁),
//   **絕不給 `allow-scripts` 或 `allow-same-origin`**:兩個一起給等於沒有 sandbox ——
//   信裡的程式就能摸到本站的 cookie 與 API。
// - srcDoc 裡多一行 CSP:就算清洗漏了什麼(CSS 的 background-image 之類),遠端資源也一律不載;
//   本人按了「顯示圖片」才放行圖片。
// - 遠端圖片預設不載(後端已把 src 拿掉),`remote_images_blocked > 0` 才出現「顯示圖片」。
// - 雲端已刪除的信照樣讀得到、附件照樣下載得到,但不能改旗標或刪除。
// - §2.6 的追加:警告(冒名 / 危險附件紅字、外部灰標)、對應客戶側欄、附件預覽、行事曆卡片、
//   列印 / 原始碼 / .eml、封存、待回覆提醒 —— 各是一個元件,這裡只排版。
// - 整塊帶 `nq-mail-print`:列印時只印這一塊(MailboxPage 的列印樣式)。

export type ReplyKind = "reply" | "replyAll" | "forward";

export interface MailReaderProps {
  detail: MailDetail;
  imagesAllowed: boolean;
  /** 雲端連不上 / 密碼被拒:會動到雲端的按鈕一律停用。 */
  cloudDisabled: boolean;
  /** 系統備份裡讀信:唯讀的檔案庫,不放星號 / 已讀 / 封存 / 刪除(讀、回覆、轉寄、列印照常)。 */
  readOnly?: boolean;
  busy: boolean;
  onBack: () => void;
  onShowImages: () => void;
  onReply: (kind: ReplyKind) => void;
  onToggleFlag: () => void;
  onToggleSeen: () => void;
  onDelete: () => void;
  /** 封存(搬到 INBOX.Archive)。 */
  onArchive: () => void;
  /** 這封信已設定的待回覆提醒(到期時間);沒有是 null。 */
  followupDue: string | null;
  onFollowupChange: (due: string | null) => void;
}

/** 檔案大小給人看的寫法。 */
export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** 放進 iframe 的整份文件。`<base target="_blank">` 讓連結開新分頁(sandbox 給了 allow-popups)。 */
export function buildSrcDoc(html: string, imagesAllowed: boolean): string {
  const img = imagesAllowed ? "data: https: http:" : "data:";
  return (
    "<!doctype html><html><head><meta charset=\"utf-8\">" +
    `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src ${img}; style-src 'unsafe-inline'">` +
    '<base target="_blank">' +
    "<style>body{margin:0;padding:4px;font-family:system-ui,-apple-system,'Noto Sans TC',sans-serif;" +
    "font-size:14px;line-height:1.6;color:#1f2328;background:#fff;overflow-wrap:anywhere}" +
    "img{max-width:100%;height:auto}table{max-width:100%}</style>" +
    `</head><body>${html}</body></html>`
  );
}

export function MailReader({
  detail,
  imagesAllowed,
  cloudDisabled,
  readOnly = false,
  busy,
  onBack,
  onShowImages,
  onReply,
  onToggleFlag,
  onToggleSeen,
  onDelete,
  onArchive,
  followupDue,
  onFollowupChange,
}: MailReaderProps) {
  const gone = detail.deleted_on_server;
  // 改旗標 / 刪除:雲端連不上或這封在雲端已經不在了,都做不了
  const flagsDisabled = cloudDisabled || gone || busy;
  const srcDoc = useMemo(
    () => (detail.html ? buildSrcDoc(detail.html, imagesAllowed) : ""),
    [detail.html, imagesAllowed],
  );
  const subject = detail.subject || "(無主旨)";

  return (
    <article
      aria-label={`信件:${subject}`}
      className="nq-mail-print flex min-w-0 flex-col gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="m-0 min-w-0 flex-1 text-lg font-semibold text-[var(--text)]">{subject}</h3>
        <Button
          size="sm"
          variant="ghost"
          icon={<ArrowLeft aria-hidden="true" size={16} />}
          onClick={onBack}
          data-back=""
          className="print:hidden"
        >
          回列表
        </Button>
      </div>

      <MailWarnings warnings={detail.warnings} />

      <div className="flex flex-col gap-0.5 text-sm text-[var(--text-2)]">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="text-[var(--text-3)]">寄件人:</span>
          {detail.from_name ? `${detail.from_name} <${detail.from_addr}>` : detail.from_addr}
          <ExternalBadge warnings={detail.warnings} />
        </span>
        {detail.to_addrs.length > 0 ? (
          <span>
            <span className="text-[var(--text-3)]">收件人:</span>
            {detail.to_addrs.join("、")}
          </span>
        ) : null}
        <span className="tabular text-[var(--text-3)]">{formatTaipeiDateTime(detail.date)}</span>
      </div>

      {gone ? (
        <p className="m-0 flex items-center gap-2 text-sm text-[var(--text-3)]">
          <Badge tone="neutral">雲端已刪除</Badge>
          這封信在雲端已經刪除了,這裡是系統備份;讀得到、附件下載得到,但不能改已讀、星號或刪除。
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2 print:hidden">
        <Button size="sm" icon={<Reply aria-hidden="true" size={16} />} disabled={cloudDisabled} onClick={() => onReply("reply")}>
          回覆
        </Button>
        <Button size="sm" icon={<ReplyAll aria-hidden="true" size={16} />} disabled={cloudDisabled} onClick={() => onReply("replyAll")}>
          全部回覆
        </Button>
        <Button size="sm" icon={<Forward aria-hidden="true" size={16} />} disabled={cloudDisabled} onClick={() => onReply("forward")}>
          轉寄
        </Button>
        {readOnly ? null : (
          <>
            <Button
              size="sm"
              aria-pressed={detail.flagged}
              icon={<Star aria-hidden="true" size={16} fill={detail.flagged ? "currentColor" : "none"} />}
              disabled={flagsDisabled}
              onClick={onToggleFlag}
            >
              星號
            </Button>
            <Button
              size="sm"
              icon={detail.seen ? <Mail aria-hidden="true" size={16} /> : <MailOpen aria-hidden="true" size={16} />}
              disabled={flagsDisabled}
              onClick={onToggleSeen}
            >
              {detail.seen ? "標未讀" : "標已讀"}
            </Button>
            <Button
              size="sm"
              icon={<Archive aria-hidden="true" size={16} />}
              disabled={flagsDisabled || detail.folder === ARCHIVE_FOLDER}
              onClick={onArchive}
            >
              封存
            </Button>
            <Button
              size="sm"
              icon={<Trash2 aria-hidden="true" size={16} />}
              disabled={flagsDisabled}
              onClick={onDelete}
            >
              刪除
            </Button>
          </>
        )}
        <FollowupControl messageId={detail.id} due={followupDue} onChange={onFollowupChange} />
      </div>

      <MailSourceTools messageId={detail.id} />
      <ClientPanel clients={detail.clients} />
      <CalendarCard messageId={detail.id} events={detail.calendar} />

      {detail.remote_images_blocked > 0 && !imagesAllowed ? (
        <p className="m-0 flex flex-wrap items-center gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--text-2)] print:hidden">
          <span>為了不讓寄件人知道你開了信,{detail.remote_images_blocked} 張遠端圖片沒有載入。</span>
          <Button size="sm" icon={<Image aria-hidden="true" size={16} />} onClick={onShowImages}>
            顯示圖片
          </Button>
        </p>
      ) : null}
      {detail.inline_images_dropped > 0 ? (
        <p className="m-0 text-xs text-[var(--text-3)]">
          有 {detail.inline_images_dropped} 張內嵌圖片太大或格式不支援,沒有顯示。
        </p>
      ) : null}

      {detail.html ? (
        <iframe
          title="信件內容"
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          srcDoc={srcDoc}
          referrerPolicy="no-referrer"
          // 底色由 srcDoc 自己給(白):信是寄件人照白底排的,深色模式下也維持原樣才讀得出來
          className="h-[60vh] min-h-64 w-full rounded-[var(--radius)] border border-[var(--border)]"
        />
      ) : detail.text ? (
        <pre className="m-0 whitespace-pre-wrap break-words font-sans text-sm text-[var(--text)]">{detail.text}</pre>
      ) : (
        <p className="m-0 text-sm text-[var(--text-3)]">(這封信沒有內文)</p>
      )}

      <AttachmentPreview messageId={detail.id} attachments={detail.attachments} />
    </article>
  );
}
